import { useQuery } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import { Box, List, ListItem, TextField, useTheme } from "@mui/material";
import { Dialog } from "components/Dialog";
import { getFullName } from "encamp-shared/src/utils/name";
import { useCallback, useEffect, useState } from "react";

import { gql } from "../../generated-graphql";
import { StaffUsersQuery } from "../../generated-graphql/graphql";
import { useDebounce } from "../../hooks/useDebounce";
import { SpinnerOverlay } from "../SpinnerOverlay";

const STAFF_USERS_QUERY = gql(`
  query StaffUsers($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    staffUsers(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        email
        person {
          id
          first
          last
        }
      }
    }
  } 
`);

type Props = {
  title?: string;
  open: boolean;
  onSelect: (userId: string) => void | Promise<void>;
  onClose: () => void;
};

export function UserSelectDialog(props: Props) {
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState<
    StaffUsersQuery["staffUsers"]["items"][0] | null
  >(null);
  const [debouncedSearch] = useDebounce(search, 1000);
  const theme = useTheme();

  const { data, loading, previousData } = useQuery(STAFF_USERS_QUERY, {
    fetchPolicy: "network-only",
    variables: {
      search: debouncedSearch,
    },
  });

  const handleClose = useCallback(() => {
    props.onClose?.();
  }, [props]);

  const handleSelect = useCallback(async () => {
    setSaving(true);
    if (selectedUser) {
      await props.onSelect(selectedUser.id);
    }
  }, [selectedUser, props]);

  useEffect(() => {
    if (!props.open) {
      setSelectedUser(null);
      setSaving(false);
      setSearch("");
    }
  }, [props.open]);

  const d = loading ? previousData : data;

  return (
    <Dialog
      scroll="paper"
      open={props.open}
      onClose={handleClose}
      fullWidth
      title={props.title ?? "Select a user"}
      primaryActionButtonProps={{
        disabled: !selectedUser || saving,
        onClick: handleSelect,
        saveText: `Select ${getFullName(selectedUser?.person)}`,
        startIcon: null,
      }}
    >
      <TextField
        placeholder="Search"
        size="small"
        sx={{ paddingRight: theme.spacing(2) }}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <List sx={{ maxHeight: "20rem", overflowY: "auto" }}>
        {d?.staffUsers.items.map((user) => (
          <ListItem
            sx={{
              cursor: "pointer",
              overflowY: "auto",
              borderTop: theme.palette.divider,
              ":hover": {
                background: theme.palette.action.hover,
              },
            }}
            onClick={() => setSelectedUser(user)}
            key={user.id}
          >
            {user.id === selectedUser?.id ? (
              <Check sx={{ color: theme.palette.success.main }} />
            ) : (
              ""
            )}
            <Box>
              {getFullName(user.person)} {"<" + user.email + ">"}
            </Box>
          </ListItem>
        ))}
      </List>
      <SpinnerOverlay loading={loading} />
    </Dialog>
  );
}
